import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'ghost'
    | 'link'
    | 'search'
    | 'blue';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  children: React.ReactNode;
  isLoading?: boolean;
  // Add any other specific props you might need, e.g., leftIcon, rightIcon
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  isLoading = false,
  className, // Allow additional classes to be passed
  ...props
}) => {
  // Base styles
  let baseStyles =
    'inline-flex items-center justify-center rounded-md font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200';

  // Variant styles
  const variantStyles = {
    primary:
      'bg-transparent text-text-primary-light hover:bg-white/10 hover:shadow-md border border-white backdrop-blur-sm focus:ring-text-secondary-light',
    secondary:
      'bg-transparent text-text-primary-light hover:bg-white/10 hover:shadow-md border border-white backdrop-blur-sm focus:ring-text-secondary-light',
    danger:
      'bg-red-500 text-white hover:bg-red-600 hover:shadow-md focus:ring-red-500 border border-white',
    ghost:
      'bg-transparent hover:bg-white/10 hover:shadow-sm text-text-primary-light dark:text-text-primary-dark focus:ring-text-secondary-light border border-white',
    link: 'text-text-secondary-light dark:text-text-secondary-dark underline-offset-4 hover:underline focus:ring-text-secondary-light',
    search:
      'bg-transparent text-text-primary-light border border-white hover:bg-white/10 hover:shadow-md backdrop-blur-sm focus:ring-2 focus:ring-gradient-white',
    blue: 'bg-transparent text-[#2A7B9B] hover:bg-[#2A7B9B]/10 border border-[#2A7B9B]/50 hover:border-[#2A7B9B] backdrop-blur-sm focus:ring-[#2A7B9B]',
  };

  // Size styles
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    icon: 'h-10 w-10', // Square button for icons
  };

  const combinedClassName = `
    ${baseStyles}
    ${variantStyles[variant]}
    ${sizeStyles[size]}
    ${isLoading ? 'cursor-wait' : ''}
    ${className || ''}
  `;

  return (
    <button
      className={combinedClassName.trim()}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {isLoading ? (
        <svg
          className='animate-spin -ml-1 mr-3 h-5 w-5 text-current'
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          ></circle>
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          ></path>
        </svg>
      ) : null}
      {children}
    </button>
  );
};

export default Button;
